import { useState, useEffect } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Badge } from "../components/ui/badge";
import { UserPlus, Trash2, Eye, EyeOff, Building2, Pencil } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";

const ROLES = [
  { value: "SS", label: "Skyworth Staff" },
  { value: "SM", label: "Skyworth Management" },
  { value: "CI", label: "CargoDash Inhouse" },
  { value: "CS", label: "CargoDash Staff" },
  { value: "CM", label: "CargoDash Management" },
  { value: "SA", label: "Accountant" },
  { value: "3PA", label: "3rd Party Agent" },
];

const ROLE_COLORS = {
  SS: "bg-blue-50 text-blue-700 border-blue-200",
  SM: "bg-blue-50 text-blue-700 border-blue-200",
  CI: "bg-teal-50 text-teal-700 border-teal-200",
  CS: "bg-teal-50 text-teal-700 border-teal-200",
  CM: "bg-teal-50 text-teal-700 border-teal-200",
  SA: "bg-violet-50 text-violet-700 border-violet-200",
  "3PA": "bg-emerald-50 text-emerald-700 border-emerald-200",
};

const COMPANY_TYPE_META = {
  SKYWORTH: { label: "Skyworth", cls: "bg-blue-50 text-blue-700 border-blue-200" },
  CARGODASH: { label: "CargoDash", cls: "bg-teal-50 text-teal-700 border-teal-200" },
  THIRD_PARTY: { label: "3rd Party", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
};

export default function UserManagement() {
  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [form, setForm] = useState({ company_id: "", name: "", email: "", password: "", role: "" });
  const [submitting, setSubmitting] = useState(false);
  const [filterCompany, setFilterCompany] = useState("all");

  // Edit dialog state — opened from the per-row pencil icon. Holds the
  // user being edited plus the form values. `original_email` is what we
  // send on the API call (so even if the admin renames the email, we
  // still target the correct user).
  const [editUser, setEditUser] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", email: "", role: "", company_id: "", password: "" });
  const [editShowPassword, setEditShowPassword] = useState(false);
  const [editSubmitting, setEditSubmitting] = useState(false);

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    try {
      const [uRes, cRes] = await Promise.all([api.get("/users"), api.get("/companies")]);
      setUsers(uRes.data || []);
      setCompanies(cRes.data || []);
    } catch { toast.error("Failed to load users / companies"); }
    setLoading(false);
  };

  const createUser = async () => {
    if (!form.company_id || !form.name || !form.email || !form.role || !form.password) {
      toast.error("Please fill all required fields");
      return;
    }
    setSubmitting(true);
    try {
      // Backend derives organization from company_id; send it for display continuity in old UIs
      const comp = companies.find((c) => c.id === form.company_id);
      await api.post("/users/create", {
        company_id: form.company_id,
        name: form.name,
        email: form.email,
        role: form.role,
        organization: comp?.name || "",
        password: form.password,
      });
      toast.success("User created successfully");
      setDialogOpen(false);
      setForm({ company_id: "", name: "", email: "", password: "", role: "" });
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create user");
    }
    setSubmitting(false);
  };

  const reassignCompany = async (email, company_id) => {
    try {
      await api.put(`/users`, { company_id }, { params: { email } });
      toast.success("Company updated");
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update company");
    }
  };

  const updateRole = async (email, role) => {
    try {
      await api.put(`/users`, { role }, { params: { email } });
      toast.success("Role updated");
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update role");
    }
  };

  const deleteUser = async (email) => {
    if (!window.confirm(`Are you sure you want to delete ${email}?`)) return;
    try {
      await api.delete(`/users/${email}`);
      toast.success("User deleted");
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete");
    }
  };

  // ---- Edit user (rename, role/company change, password reset) ----------
  const openEdit = (u) => {
    setEditUser(u);
    setEditForm({
      name: u.name || "",
      email: u.email || "",
      role: u.role || "",
      company_id: u.company_id || "",
      password: "",
    });
    setEditShowPassword(false);
  };

  const closeEdit = () => {
    setEditUser(null);
    setEditForm({ name: "", email: "", role: "", company_id: "", password: "" });
    setEditShowPassword(false);
  };

  const saveEdit = async () => {
    if (!editUser) return;
    if (!editForm.name.trim() || !editForm.email.trim() || !editForm.role || !editForm.company_id) {
      toast.error("Name, email, role and company are required");
      return;
    }
    const payload = {};
    if (editForm.name !== editUser.name) payload.name = editForm.name.trim();
    if (editForm.role !== editUser.role) payload.role = editForm.role;
    if (editForm.company_id !== (editUser.company_id || "")) payload.company_id = editForm.company_id;
    if (editForm.email.trim().toLowerCase() !== (editUser.email || "").toLowerCase()) {
      payload.email = editForm.email.trim().toLowerCase();
    }
    if (editForm.password) payload.password = editForm.password;

    if (Object.keys(payload).length === 0) {
      toast.info("No changes to save");
      return;
    }

    setEditSubmitting(true);
    try {
      // Use the query-string variant of PUT /users so emails (which contain
      // `@`) don't get mangled by Cloudflare's WAF on the production domain.
      await api.put(`/users`, payload, { params: { email: editUser.email } });
      toast.success(payload.password ? "User updated — password reset" : "User updated");
      closeEdit();
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update user");
    }
    setEditSubmitting(false);
  };

  const visibleUsers = filterCompany === "all"
    ? users
    : users.filter((u) => (u.company_id || "") === filterCompany);

  // Group users by company for a company-grouped table (matches user's requested workflow)
  const groupedByCompany = companies.map((c) => ({
    company: c,
    users: users.filter((u) => u.company_id === c.id),
  }));
  const orphanUsers = users.filter((u) => !u.company_id);

  if (loading) return <div className="text-slate-500 p-8">Loading users...</div>;

  return (
    <div data-testid="user-management-page" className="space-y-6 max-w-6xl">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading font-bold text-2xl tracking-tight text-slate-900 flex items-center gap-2">
            <UserPlus size={24} className="text-teal-600" /> User Management
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            Workflow: <span className="text-slate-700 font-medium">Company → User → Role</span>. Every user belongs to a company.
          </p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(v) => { setDialogOpen(v); if (!v) setForm({ company_id: "", name: "", email: "", password: "", role: "" }); }}>
          <DialogTrigger asChild>
            <Button data-testid="create-user-btn" className="bg-teal-600 hover:bg-teal-700 text-white">
              <UserPlus size={16} className="mr-2" /> Create User
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-white border-slate-200 max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading text-slate-900">Create New User</DialogTitle>
              <DialogDescription className="text-xs text-slate-500">
                Follow the steps: pick a company, enter the user's details, then assign a role.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              {/* Step 1: Company */}
              <div className="space-y-2">
                <Label className="text-[10px] uppercase tracking-[0.2em] text-slate-500">
                  <span className="inline-block w-5 h-5 rounded-full bg-teal-600 text-white text-[10px] text-center leading-5 mr-2">1</span>
                  Company *
                </Label>
                <Select value={form.company_id} onValueChange={(v) => setForm({ ...form, company_id: v })}>
                  <SelectTrigger data-testid="user-company-select" className="bg-white border-slate-300">
                    <SelectValue placeholder={companies.length ? "Select company" : "No companies — add one in System Config"} />
                  </SelectTrigger>
                  <SelectContent>
                    {companies.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        <div className="flex items-center gap-2">
                          <Building2 size={12} className="text-teal-600" />
                          <span>{c.name}</span>
                          <Badge variant="outline" className={`text-[9px] uppercase ${COMPANY_TYPE_META[c.type]?.cls || ""}`}>
                            {COMPANY_TYPE_META[c.type]?.label || "3rd Party"}
                          </Badge>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Step 2: Name + email + password (only enabled once company picked) */}
              <div className={`space-y-4 ${!form.company_id ? "opacity-50 pointer-events-none" : ""}`}>
                <div className="space-y-2">
                  <Label className="text-[10px] uppercase tracking-[0.2em] text-slate-500">
                    <span className="inline-block w-5 h-5 rounded-full bg-teal-600 text-white text-[10px] text-center leading-5 mr-2">2</span>
                    Full Name *
                  </Label>
                  <Input
                    data-testid="user-name-input"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="Enter full name"
                    className="bg-white border-slate-300"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-[10px] uppercase tracking-[0.2em] text-slate-500">Email Address *</Label>
                  <Input
                    data-testid="user-email-input"
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="name@company.co"
                    className="bg-white border-slate-300"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-[10px] uppercase tracking-[0.2em] text-slate-500">Password *</Label>
                  <div className="relative">
                    <Input
                      data-testid="user-password-input"
                      type={showPassword ? "text" : "password"}
                      value={form.password}
                      onChange={(e) => setForm({ ...form, password: e.target.value })}
                      placeholder="Initial password"
                      className="bg-white border-slate-300 pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400"
                    >
                      {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>

                {/* Step 3: Role */}
                <div className="space-y-2">
                  <Label className="text-[10px] uppercase tracking-[0.2em] text-slate-500">
                    <span className="inline-block w-5 h-5 rounded-full bg-teal-600 text-white text-[10px] text-center leading-5 mr-2">3</span>
                    Role *
                  </Label>
                  <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                    <SelectTrigger data-testid="user-role-select" className="bg-white border-slate-300">
                      <SelectValue placeholder="Select predefined role" />
                    </SelectTrigger>
                    <SelectContent>
                      {ROLES.map((r) => (
                        <SelectItem key={r.value} value={r.value}>
                          <span className="font-mono text-xs mr-2">{r.value}</span>
                          {r.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <Button
                data-testid="submit-user-btn"
                onClick={createUser}
                disabled={submitting || !form.company_id || !form.name || !form.email || !form.role || !form.password}
                className="w-full bg-teal-600 hover:bg-teal-700 text-white"
              >
                {submitting ? "Creating..." : "Create User"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filter */}
      <div className="flex items-center gap-3">
        <Label className="text-xs text-slate-500">Filter by company:</Label>
        <Select value={filterCompany} onValueChange={setFilterCompany}>
          <SelectTrigger data-testid="filter-company-select" className="bg-white border-slate-300 h-9 w-64">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All companies</SelectItem>
            {companies.map((c) => (
              <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <span className="text-xs text-slate-400">{visibleUsers.length} user(s)</span>
      </div>

      {/* Grouped by Company */}
      <div className="space-y-4">
        {(filterCompany === "all" ? groupedByCompany : groupedByCompany.filter((g) => g.company.id === filterCompany)).map(({ company, users: companyUsers }) => (
          <div key={company.id} className="bg-white rounded-xl shadow-sm overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 bg-slate-50 border-b border-slate-200">
              <div className="flex items-center gap-2">
                <Building2 size={16} className="text-teal-600" />
                <span className="font-semibold text-slate-800">{company.name}</span>
                <Badge variant="outline" className={`text-[10px] uppercase tracking-wider ${COMPANY_TYPE_META[company.type]?.cls || ""}`}>
                  {COMPANY_TYPE_META[company.type]?.label || "3rd Party"}
                </Badge>
                {company.is_builtin && (
                  <Badge variant="outline" className="text-[9px] uppercase bg-slate-100 text-slate-500 border-slate-200">built-in</Badge>
                )}
              </div>
              <span className="text-xs text-slate-500">{companyUsers.length} user(s)</span>
            </div>
            {companyUsers.length === 0 ? (
              <div className="p-6 text-center text-slate-400 text-sm">No users in this company</div>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-100 text-slate-500 text-[10px] uppercase tracking-wider">
                    <th className="text-left p-3">Name</th>
                    <th className="text-left p-3">Email</th>
                    <th className="text-left p-3">Role</th>
                    <th className="text-left p-3">Reassign Company</th>
                    <th className="text-right p-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {companyUsers.map((u) => (
                    <tr key={u.email} data-testid={`user-row-${u.email}`} className="border-b border-slate-50 hover:bg-slate-50 transition-colors">
                      <td className="p-3 font-medium text-slate-800">{u.name}</td>
                      <td className="p-3 text-slate-600">{u.email}</td>
                      <td className="p-3">
                        <Select value={u.role} onValueChange={(v) => updateRole(u.email, v)}>
                          <SelectTrigger data-testid={`role-select-${u.email}`} className={`h-8 w-48 bg-white border-slate-200 text-xs ${ROLE_COLORS[u.role] || ""}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {ROLES.map((r) => (
                              <SelectItem key={r.value} value={r.value}>
                                <span className="font-mono text-[10px] mr-2">{r.value}</span>
                                {r.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="p-3">
                        <Select value={u.company_id || ""} onValueChange={(v) => reassignCompany(u.email, v)}>
                          <SelectTrigger data-testid={`reassign-company-${u.email}`} className="h-8 w-52 bg-white border-slate-200 text-xs">
                            <SelectValue placeholder="Unassigned" />
                          </SelectTrigger>
                          <SelectContent>
                            {companies.map((c) => (
                              <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="p-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            data-testid={`edit-user-${u.email}`}
                            variant="ghost"
                            size="sm"
                            onClick={() => openEdit(u)}
                            className="text-slate-500 hover:text-teal-700 hover:bg-teal-50"
                            title="Edit user / reset password"
                          >
                            <Pencil size={14} />
                          </Button>
                          <Button
                            data-testid={`delete-user-${u.email}`}
                            variant="ghost"
                            size="sm"
                            onClick={() => deleteUser(u.email)}
                            className="text-red-400 hover:text-red-600 hover:bg-red-50"
                          >
                            <Trash2 size={14} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        ))}

        {filterCompany === "all" && orphanUsers.length > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl overflow-hidden">
            <div className="px-4 py-3 border-b border-amber-200">
              <span className="font-semibold text-amber-800 text-sm">⚠ Unassigned users ({orphanUsers.length})</span>
              <span className="text-xs text-amber-700 ml-2">Assign each to a company below.</span>
            </div>
            <table className="w-full text-sm">
              <tbody>
                {orphanUsers.map((u) => (
                  <tr key={u.email} className="border-b border-amber-100">
                    <td className="p-3 font-medium text-slate-800">{u.name}</td>
                    <td className="p-3 text-slate-600">{u.email}</td>
                    <td className="p-3">
                      <Badge variant="outline" className={`text-[10px] uppercase ${ROLE_COLORS[u.role] || ""}`}>{u.role}</Badge>
                    </td>
                    <td className="p-3">
                      <Select value="" onValueChange={(v) => reassignCompany(u.email, v)}>
                        <SelectTrigger data-testid={`reassign-orphan-${u.email}`} className="h-8 w-52 bg-white border-amber-300 text-xs">
                          <SelectValue placeholder="Pick a company" />
                        </SelectTrigger>
                        <SelectContent>
                          {companies.map((c) => (
                            <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="p-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEdit(u)}
                          className="text-slate-500 hover:text-teal-700 hover:bg-teal-50"
                          title="Edit user / reset password"
                        >
                          <Pencil size={14} />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => deleteUser(u.email)} className="text-red-400 hover:text-red-600 hover:bg-red-50">
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Edit User Dialog — admin can change name/email/role/company and
          reset the password. Triggered by the per-row pencil icon. */}
      <Dialog open={!!editUser} onOpenChange={(v) => { if (!v) closeEdit(); }}>
        <DialogContent className="bg-white border-slate-200 max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-slate-900">Edit User</DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              Update profile details, change role / company, or reset the password.
              {editUser?.hub_managed && (
                <span className="block mt-2 text-amber-700">
                  This user signs in via BlackScott Hub — email & password are managed there.
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          {editUser && (
            <div className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label className="text-[10px] uppercase tracking-[0.2em] text-slate-500">Full Name *</Label>
                <Input
                  data-testid="edit-user-name-input"
                  value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  className="bg-white border-slate-300"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-[10px] uppercase tracking-[0.2em] text-slate-500">Email Address *</Label>
                <Input
                  data-testid="edit-user-email-input"
                  type="email"
                  value={editForm.email}
                  onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                  disabled={!!editUser.hub_managed}
                  className="bg-white border-slate-300 disabled:bg-slate-50"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-[10px] uppercase tracking-[0.2em] text-slate-500">Company *</Label>
                <Select value={editForm.company_id} onValueChange={(v) => setEditForm({ ...editForm, company_id: v })}>
                  <SelectTrigger data-testid="edit-user-company-select" className="bg-white border-slate-300">
                    <SelectValue placeholder="Select company" />
                  </SelectTrigger>
                  <SelectContent>
                    {companies.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        <div className="flex items-center gap-2">
                          <Building2 size={12} className="text-teal-600" />
                          <span>{c.name}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-[10px] uppercase tracking-[0.2em] text-slate-500">Role *</Label>
                <Select value={editForm.role} onValueChange={(v) => setEditForm({ ...editForm, role: v })}>
                  <SelectTrigger data-testid="edit-user-role-select" className="bg-white border-slate-300">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ROLES.map((r) => (
                      <SelectItem key={r.value} value={r.value}>
                        <span className="font-mono text-xs mr-2">{r.value}</span>
                        {r.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2 pt-3 border-t border-slate-100">
                <Label className="text-[10px] uppercase tracking-[0.2em] text-slate-500">
                  Reset Password <span className="text-slate-400 normal-case lowercase tracking-normal">(leave blank to keep current)</span>
                </Label>
                <div className="relative">
                  <Input
                    data-testid="edit-user-password-input"
                    type={editShowPassword ? "text" : "password"}
                    value={editForm.password}
                    onChange={(e) => setEditForm({ ...editForm, password: e.target.value })}
                    placeholder={editUser.hub_managed ? "Hub-managed — N/A" : "New password (min 6 chars)"}
                    disabled={!!editUser.hub_managed}
                    className="bg-white border-slate-300 pr-10 disabled:bg-slate-50"
                  />
                  {!editUser.hub_managed && (
                    <button
                      type="button"
                      onClick={() => setEditShowPassword(!editShowPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400"
                    >
                      {editShowPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-2 pt-2">
                <Button
                  variant="outline"
                  onClick={closeEdit}
                  className="flex-1 border-slate-300 text-slate-600"
                >
                  Cancel
                </Button>
                <Button
                  data-testid="save-edit-user-btn"
                  onClick={saveEdit}
                  disabled={editSubmitting}
                  className="flex-1 bg-teal-600 hover:bg-teal-700 text-white"
                >
                  {editSubmitting ? "Saving..." : "Save Changes"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
